import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Customer,
  FpCompany,
  FpTheme,
  Job,
  PrimaryTheme,
  usePreloaderContext
} from '@flicent/fieldpulse-react-component-library-frontend';

import { getJobReportDataRequest, signAndAcceptJobPreviewRequest } from '@/services';
import { IS_PROD, JOB_PUBLIC_LINK_STATUS } from '@/constants';
import { JobReport, JobPageContent } from '@/components';
import { MetaData } from '@/interfaces';
import { JobPublicLinkStatus } from '@/types';

import { MOCK_META_DATA } from '../mock-data.constant';


const JobReportPreview = () => {
  const { customerId } = useParams();
  const [searchParams] = useSearchParams();

  const [metaData, setMetaData] = useState<MetaData | null>(null);
  const [theme, setTheme] = useState<FpTheme>(PrimaryTheme);
  const preloaderContext = usePreloaderContext();

  const [error, setError] = useState<string | null>(null);
  const [signatureResponseStatus, setSignatureResponseStatus] = useState<JobPublicLinkStatus>();
  const [pdfUrl, setPdfUrl] = useState<string>('');

  const token = searchParams.get('token');
  const jobIdsSearchParams = searchParams.get('job_ids');

  const acceptPreviewSignatures = async (signatures: string[]) => {
    preloaderContext.showMiddlePagePreloader();

    const jobIds: string[] = jobIdsSearchParams!.split(',');

    const acceptSignaturesResponse = await signAndAcceptJobPreviewRequest({
      token: token!,
      customerId: customerId!,
      signatures,
      jobIds,
    }).finally(() => {
      preloaderContext.hideMiddlePagePreloader();
    });

    setSignatureResponseStatus(acceptSignaturesResponse.status);
    setPdfUrl(acceptSignaturesResponse.pdf_url);

    return acceptSignaturesResponse;
  };

  // Fetch metadata based on the location params
  useEffect(() => {
    if (!customerId || !token || !jobIdsSearchParams) {
      return;
    }

    const jobIds: string[] = jobIdsSearchParams.split(',');

    preloaderContext.showFullPagePreloader();

    getJobReportDataRequest({
      customerId,
      token,
      jobIds,
    })
      .then(({ meta, theme }) => {
        setMetaData(meta);
        setTheme(theme);
      })
      .catch((error) => {
        if (IS_PROD) {
          setError(error.message);
        } else {
          setMetaData(MOCK_META_DATA);
        }
      })
      .finally(() => {
        preloaderContext.hideFullPagePreloader();
      });
  }, [customerId, token, jobIdsSearchParams, searchParams]);


  let contentComponent;

  if (metaData) {
    const isSignaturesAccepted = signatureResponseStatus === JOB_PUBLIC_LINK_STATUS.accepted;

    const jobs: Job[] = metaData.jobs;
    const company: FpCompany = metaData.company;
    const customer: Customer = metaData.customer;

    contentComponent = <JobReport
      company={ company }
      customer={ customer }
      jobs={ jobs }
      isShowSignatureRequest={ customer.is_job_show_signature_request }
      isSignaturesAccepted={ isSignaturesAccepted }
      acceptedSignaturesPdfUrl={ pdfUrl }
      onSignaturesAccept={ acceptPreviewSignatures }
    />;
  }


  return (
    <JobPageContent isLoading={ !metaData }
                    companyJobName={ metaData ? metaData.company.job_name : null }
                    error={ error }
                    theme={ theme }>
      { contentComponent }
    </JobPageContent>
  );
};


export default JobReportPreview;
